import { PersonIcon } from "@radix-ui/react-icons";
import { Select } from "antd";
import type { BaseSelectRef } from "rc-select";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { MagnifyingGlass } from "../../assets/icons/MagnifyingGlass";
import { css } from "../../styles";
import { IOption } from "../SelectDropdown";

export interface ISearchOption extends IOption {
  searchText?: string;
  secondary?: string;
}

type SearchFieldProps = {
  value?: string | null;
  options: ISearchOption[];
  placeholder?: string;
  onValueChange: (selected: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const searchFieldInactiveCss = css({
  fontFamily: "$openSans !important",
  backgroundColor: "rgba(232, 228, 228, 0.3464) !important",
  border: "2px solid rgba(232, 228, 228, 0) !important",
  borderRadius: "21px",
  color: "#212121 !important",
  cursor: "pointer !important",

  ".ant-select-arrow": {
    position: "absolute",
    top: "19px",
    left: "10px",
  },

  ".ant-select-selector": {
    fontFamily: "$openSans !important",
    paddingLeft: "40px !important",
    cursor: "pointer !important",
  },

  ".ant-select-selection-search": {
    fontFamily: "$openSans !important",
    cursor: "pointer !important",
  },

  ".ant-select-selection-search-input": {
    cursor: "pointer !important",
  },

  "&:hover": {
    backgroundColor: "rgba(244, 244, 244, 1) !important",
    border: "2px solid rgba(244, 244, 244, 1) !important",
  },
});

const searchFieldActiveCss = css({
  fontFamily: "$openSans !important",
  backgroundColor: "#FFFFFF",
  border: "2px solid",
  borderColor: "var(--colors-navbar-search-bar-active-border)",
  borderRadius: "21px",
  color: "#212121",

  ".ant-select-arrow": {
    position: "absolute",
    top: "19px",
    left: "10px",
  },

  ".ant-select-selector": {
    fontFamily: "$openSans !important",
    paddingLeft: "40px !important",
  },

  ".ant-select-selection-search": {
    fontFamily: "$openSans !important",
  },

  "&.ant-select-open": {
    borderRadius: "21px 21px 0 0",
    borderBottomColor: "transparent",
  },
});

// The popup is rendered inside `popupContainerCss` (a relative wrapper around
// the trigger) via `getPopupContainer`. Pinning the popup to the wrapper
// edges with `!important` overrides — instead of relying on antd's sub-pixel
// JS positioning — guarantees the popup's width and x-position match the
// trigger's exactly, so the side borders always connect. `top` overlaps the
// trigger by 1px so the bottom of the input meets the top of the popup
// without a visible vertical seam.
const popupCss = css({
  fontFamily: "$openSans !important",
  zIndex: "2000 !important",
  boxSizing: "border-box",
  position: "absolute !important",
  left: "0 !important",
  right: "0 !important",
  top: "calc(100% - 1px) !important",
  width: "auto !important",
  border: "2px solid var(--colors-navbar-search-bar-active-border)",
  borderTop: "none",
  borderRadius: "0 0 21px 21px",
  backgroundColor: "#FFFFFF",
  boxShadow: "none !important",
  padding: "0 !important",
  overflow: "hidden",

  ".ant-select-item-empty": {
    color: "#666666 !important",
    fontSize: "14px",
    fontWeight: "400",
    fontFamily: "$openSans !important",
    padding: "8px 16px",
  },

  ".ant-select-item": {
    padding: "10px 16px",
  },

  ".ant-select-item-option": {
    borderRadius: "0",
  },

  ".ant-select-item-option-content": {
    color: "#212121 !important",
  },
});

// Wraps the trigger so we can render the popup inside it via
// `getPopupContainer`. `display: flex` collapses the inline-block descender
// space of the antd Select so the wrapper height matches the trigger height
// exactly — both the trigger and popup are then sized off the wrapper's
// width, keeping their side borders pixel-aligned.
const popupContainerCss = css({
  position: "relative",
  width: "100%",
  display: "flex",
});

const optionRowCss = css({
  display: "flex",
  alignItems: "center",
  fontFamily: "$openSans",
  fontSize: "14px",
  color: "#212121",

  ".search-option-icon": {
    color: "#9E9E9E",
    width: "16px",
    height: "16px",
    marginRight: "16px",
    flexShrink: 0,
  },

  ".search-option-label": {
    color: "#212121",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  ".search-option-secondary": {
    color: "#9E9E9E",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginLeft: "28px",
    flexShrink: 0,
  },
});

// `popupCss` overrides the popup's position via CSS, so these placements are
// effectively a no-op — kept as a defense-in-depth so if the override ever
// fails the popup still sits flush against the trigger instead of inheriting
// rc-select's hardcoded 4px offset.
const flushPlacements = {
  bottomLeft: {
    points: ["tl", "bl"],
    offset: [0, 0],
    overflow: { adjustX: 0, adjustY: 1 },
    htmlRegion: "scroll" as const,
  },
  bottomRight: {
    points: ["tr", "br"],
    offset: [0, 0],
    overflow: { adjustX: 0, adjustY: 1 },
    htmlRegion: "scroll" as const,
  },
  topLeft: {
    points: ["bl", "tl"],
    offset: [0, 0],
    overflow: { adjustX: 0, adjustY: 1 },
    htmlRegion: "scroll" as const,
  },
  topRight: {
    points: ["br", "tr"],
    offset: [0, 0],
    overflow: { adjustX: 0, adjustY: 1 },
    htmlRegion: "scroll" as const,
  },
};

function optionMatches(
  option: ISearchOption | undefined,
  input: string
): boolean {
  if (!option) return false;
  const needle = input.trim().toLowerCase();
  if (!needle) return true;
  const haystack =
    option.searchText ?? `${option.value ?? ""} ${option.label ?? ""}`;
  return haystack.toLowerCase().includes(needle);
}

export function SearchField({
  value,
  placeholder,
  options,
  onBlur,
  onFocus,
  onValueChange,
}: SearchFieldProps) {
  const { t } = useTranslation();

  const [isActive, setIsActive] = useState(false);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [visibleOptions, setVisibleOptions] =
    useState<ISearchOption[]>(options);

  const searchFieldRef = useRef<BaseSelectRef>(null);
  const popupContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVisibleOptions(options);
  }, [options]);

  const handleSearch = (nextSearchValue: string) => {
    setSearchValue(nextSearchValue);
    setVisibleOptions(
      options.filter((option) => optionMatches(option, nextSearchValue))
    );
  };

  const handleFocus = () => {
    setIsActive(true);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setIsActive(false);
    setSearchValue(null);
    if (onBlur) onBlur();
  };

  const handleOnChange = (selectedValue: string) => {
    setSearchValue(null);
    searchFieldRef.current?.blur();
    onValueChange(selectedValue);
  };

  return (
    <div ref={popupContainerRef} className={popupContainerCss()}>
      <Select<string, ISearchOption>
        ref={searchFieldRef}
        showSearch
        value={value as string | undefined}
        suffixIcon={<MagnifyingGlass />}
        style={{ width: "100%", height: "40px" }}
        placeholder={placeholder}
        notFoundContent={t("common_noResultsFound")}
        options={visibleOptions}
        variant="borderless"
        defaultActiveFirstOption={false}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSearch={handleSearch}
        onChange={handleOnChange}
        listHeight={300}
        className={isActive ? searchFieldActiveCss() : searchFieldInactiveCss()}
        classNames={{ popup: { root: popupCss() } }}
        filterOption={(input, option) => optionMatches(option, input)}
        optionRender={(option) => {
          const data = option.data as ISearchOption;
          return (
            <div className={optionRowCss()}>
              <PersonIcon className="search-option-icon" />
              <span className="search-option-label">{data.label}</span>
              {data.secondary ? (
                <span className="search-option-secondary">
                  {data.secondary}
                </span>
              ) : null}
            </div>
          );
        }}
        open={searchValue !== null && searchValue.length > 0}
        builtinPlacements={flushPlacements}
        transitionName=""
        getPopupContainer={() => popupContainerRef.current ?? document.body}
      />
    </div>
  );
}
